'use client';

import { useRef } from 'react';
import dynamic from 'next/dynamic';

import Breadcrumbs from '@/components/ui/Breadcrumbs';
import Container from '@/components/ui/Container';
import RichText from '@/components/ui/RichText';
import Text from '@/components/ui/Text';
import ArticleMeta from '@/features/blog/detail/ArticleMeta';
import ArticleNavigation from '@/features/blog/detail/ArticleNavigation';
import RelatedArticles from '@/features/blog/detail/RelatedArticles';
import TableOfContents from '@/features/blog/detail/TableOfContents';
import { ArticleDetail } from '@/lib/contentful/api/getArticle';
import { BLOG_I18N } from '@/lib/i18n/blogI18n';
import type { AppLocale } from '@/lib/i18n/locales';

const ReadingProgress = dynamic(
  () => import('@/components/ui/ReadingProgress'),
  { ssr: false },
);

const BlogPost = ({ article, locale, minRead }: BlogPostProps) => {
  const { title, date, author, content, prev, next, tags, relatedArticles } =
    article;
  const articleRef = useRef<HTMLElement>(null);
  const t = BLOG_I18N[locale];

  return (
    <>
      <ReadingProgress target={articleRef} />
      <Container className="py-section-y-sm tablet:py-section-y">
        <article ref={articleRef}>
          <Breadcrumbs
            items={[
              { label: t.breadcrumbHome, href: `/${locale}` },
              { label: t.breadcrumbBlog, href: `/${locale}/blog` },
              { label: title },
            ]}
            locale={locale}
          />

          <header className="mb-10">
            {tags.length > 0 && <ArticleTags tags={tags} />}
            <Text as="h1" variant="sectionTitle" className="mb-4">
              {title}
            </Text>
            <ArticleMeta author={author} date={date} minRead={minRead} t={t} />
          </header>

          <div className="flex gap-16">
            <div className="min-w-0 flex-1">
              {content && <RichText richTextDocument={content} />}
            </div>
            {content && <TableOfContents document={content} />}
          </div>

          <RelatedArticles articles={relatedArticles} />

          <ArticleNavigation prev={prev} next={next} locale={locale} t={t} />
        </article>
      </Container>
    </>
  );
};

export default BlogPost;

type BlogPostProps = {
  article: ArticleDetail;
  locale: AppLocale;
  minRead: number;
};

const ArticleTags = ({ tags }: { tags: ArticleDetail['tags'] }) => (
  <div className="flex flex-wrap gap-2 mb-4">
    {tags.map((tag) => (
      <span
        key={tag.id}
        className="text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full border border-primary text-primary"
      >
        {tag.name}
      </span>
    ))}
  </div>
);
